/**
 * EmailHealthPage — Admin diagnose voor verificatie-mail verzendstatus.
 *
 * Toont:
 *  - Globale toggle status (EMAILS_ENABLED)
 *  - SMTP-config (host, from)
 *  - Aantallen: users in periode, verified, sent_ok, send_error, no_attempt
 *  - Lijst van failures + accounts zonder verzendpoging
 *  - "Verstuur ontbrekende mails opnieuw" bulk-actie knop
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Mail, CheckCircle2, AlertCircle, Clock, RefreshCw, Send,
  Server, Loader2,
} from 'lucide-react';

export default function EmailHealthPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const [bulkRunning, setBulkRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('get', `/api/admin/email-health?days=${days}`);
      setData(r.data);
    } catch (e) {
      toast.error('Fout bij laden e-mail diagnose');
    }
    setLoading(false);
  }, [api, days]);

  useEffect(() => { load(); }, [load]);

  const runBulkResend = async () => {
    if (!confirm(`Verstuur opnieuw naar alle ${data?.totals?.no_attempt + data?.totals?.send_error || 0} ontbrekende accounts? Dit kan tot ~10 minuten duren (6 sec per mail).`)) {
      return;
    }
    setBulkRunning(true);
    try {
      const r = await api('post', `/api/admin/resend-verification-bulk?days=${days}`);
      toast.success(`Klaar: ${r.data.sent} verzonden, ${r.data.failed} mislukt (${r.data.checked} gecheckt)`);
      load();
    } catch (e) {
      toast.error('Bulk verzend mislukt');
    }
    setBulkRunning(false);
  };

  if (loading || !data) {
    return (
      <div className="p-8 text-white/60 flex items-center gap-2">
        <Loader2 className="animate-spin" size={16} /> Laden…
      </div>
    );
  }

  const t = data.totals || {};
  const enabled = data.emails_enabled;

  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3" style={{ fontFamily: "'Sora', sans-serif" }}>
            <Mail size={26} className="text-[#D4A017]" /> E-mail diagnostiek
          </h1>
          <p className="text-white/60 text-sm mt-1">
            Verzendstatus van verificatie-mails bij nieuwe accounts (laatste {days} dagen).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
            data-testid="period-select"
          >
            <option value={7}>Laatste 7 dagen</option>
            <option value={14}>Laatste 14 dagen</option>
            <option value={30}>Laatste 30 dagen</option>
            <option value={90}>Laatste 90 dagen</option>
          </select>
          <button
            onClick={load}
            className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5"
            data-testid="refresh-btn"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Status banner ───────────────────────────────────────── */}
      <div
        className={`rounded-xl p-5 mb-6 border ${
          enabled
            ? 'bg-emerald-500/10 border-emerald-500/30'
            : 'bg-red-500/10 border-red-500/30'
        }`}
        data-testid="status-banner"
      >
        <div className="flex items-start gap-3">
          {enabled ? (
            <CheckCircle2 size={22} className="text-emerald-400 shrink-0 mt-0.5" />
          ) : (
            <AlertCircle size={22} className="text-red-400 shrink-0 mt-0.5" />
          )}
          <div>
            <p className={`font-bold ${enabled ? 'text-emerald-300' : 'text-red-300'}`}>
              EMAILS_ENABLED = {enabled ? 'TRUE' : 'FALSE'}
            </p>
            {enabled ? (
              <p className="text-white/70 text-sm mt-1">
                Mails worden actief verstuurd via {data.smtp_host} (afzender: {data.smtp_from}).
              </p>
            ) : (
              <p className="text-white/70 text-sm mt-1">
                <strong>Mails staan gepauzeerd.</strong> Klanten ontvangen geen verificatie-mail.
                Zet <code className="bg-black/30 px-1.5 py-0.5 rounded">EMAILS_ENABLED=true</code> in Emergent Secrets en redeploy.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Totalen ────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="Accounts in periode" value={t.users_in_period || 0} color="text-white" icon={Server} />
        <StatCard label="Geverifieerd" value={t.verified || 0} color="text-emerald-400" icon={CheckCircle2} />
        <StatCard label="Mail succes" value={t.sent_ok || 0} color="text-blue-400" icon={Send} />
        <StatCard label="Verzend-fouten" value={t.send_error || 0} color="text-red-400" icon={AlertCircle} />
        <StatCard label="Geen poging" value={t.no_attempt || 0} color="text-amber-400" icon={Clock} />
      </div>

      {/* ── Bulk-actie ─────────────────────────────────────────── */}
      {((t.send_error || 0) + (t.no_attempt || 0)) > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <p className="font-bold text-amber-300">
                {(t.send_error || 0) + (t.no_attempt || 0)} accounts wachten op verificatie-mail
              </p>
              <p className="text-white/70 text-sm mt-1">
                Klik hieronder om voor élk niet-bezorgd account een nieuwe verificatie-mail te versturen.
                Token wordt vernieuwd zodat oude links niet leaken.
              </p>
            </div>
            <button
              onClick={runBulkResend}
              disabled={bulkRunning || !enabled}
              className="bg-[#D4A017] hover:bg-[#B8860B] disabled:opacity-50 disabled:cursor-not-allowed text-[#0f172a] font-bold px-5 py-3 rounded-xl flex items-center gap-2"
              data-testid="bulk-resend-btn"
            >
              {bulkRunning ? (
                <><Loader2 size={16} className="animate-spin" /> Bezig…</>
              ) : (
                <><Send size={16} /> Verstuur opnieuw</>
              )}
            </button>
          </div>
          {!enabled && (
            <p className="text-red-300 text-xs mt-3">
              ⚠️ Eerst EMAILS_ENABLED=true zetten op productie en redeployen.
            </p>
          )}
        </div>
      )}

      {/* ── Failures lijst ─────────────────────────────────────── */}
      {data.recent_failures?.length > 0 && (
        <div className="bg-white/5 rounded-xl p-5 mb-6" data-testid="failures-section">
          <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <AlertCircle size={18} className="text-red-400" /> Verzend-fouten ({data.recent_failures.length})
          </h2>
          <div className="space-y-2">
            {data.recent_failures.map((f, i) => (
              <div key={i} className="bg-black/30 rounded-lg p-3 border border-red-500/20" data-testid={`failure-${i}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-white font-mono text-sm">{f.email}</p>
                    <p className="text-white/50 text-xs">{f.name} · {f.created_at.slice(0, 19).replace('T', ' ')}</p>
                  </div>
                  <span className="text-amber-400 text-xs bg-amber-500/10 px-2 py-1 rounded">
                    {f.attempts} poging{f.attempts !== 1 ? 'en' : ''}
                  </span>
                </div>
                {f.error && (
                  <p className="text-red-300 text-xs mt-2 font-mono bg-red-500/10 p-2 rounded">{f.error}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── No-attempt lijst ───────────────────────────────────── */}
      {data.recent_no_attempt?.length > 0 && (
        <div className="bg-white/5 rounded-xl p-5" data-testid="no-attempt-section">
          <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <Clock size={18} className="text-amber-400" /> Geen verzendpoging ({data.recent_no_attempt.length})
          </h2>
          <p className="text-white/60 text-xs mb-3">
            Deze accounts zijn aangemaakt vóór de e-mail tracking-fix óf de mail-functie was gepauzeerd. Gebruik &quot;Verstuur opnieuw&quot; om alsnog te versturen.
          </p>
          <div className="space-y-2">
            {data.recent_no_attempt.map((u, i) => (
              <div key={i} className="bg-black/30 rounded-lg p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`no-attempt-${i}`}>
                <div>
                  <p className="text-white font-mono text-sm">{u.email}</p>
                  <p className="text-white/50 text-xs">{u.name} · {u.created_at.slice(0, 19).replace('T', ' ')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/5">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className={color} />
        <p className="text-white/50 text-xs uppercase tracking-wide font-semibold">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color}`} style={{ fontFamily: "'Sora', sans-serif" }}>
        {value}
      </p>
    </div>
  );
}
